import { useMemo, useState } from 'react';
import { resolveModelPricing, simulateModelSwitch, type SpendSummary } from '@ace-tracker/shared';
import { SPEND_METRIC_INFO } from '../metricInfo';
import { useSettings } from '../settings';
import { InfoTooltip } from './InfoTooltip';
import { TOOL_LABELS } from './Sidebar';
import { TableCard } from './TableCard';

const DAY_MS = 24 * 60 * 60 * 1000;
const fieldClass =
  'min-h-[44px] w-full rounded-input border border-border bg-surface px-3 text-sm text-text-default focus-visible:outline focus-visible:outline-2 focus-visible:outline-sky-blue';

/**
 * What-if model switch (E1.5): re-prices one model's real token mix in the
 * range at another catalog model's rates, plus each model's cost per session.
 * All arithmetic is simulateModelSwitch in shared/, so it matches the tests.
 */
export function WhatIfSimulator({ summary, money }: { summary: SpendSummary; money: (value: number) => string }) {
  const { data: settings } = useSettings();
  const sources = summary.byModel;
  const targets = useMemo(
    () => [...(settings?.models ?? [])].sort((a, b) => a.provider.localeCompare(b.provider) || a.model.localeCompare(b.model)),
    [settings],
  );
  const [fromKey, setFromKey] = useState<string>('');
  const [to, setTo] = useState<string>('');
  const [percent, setPercent] = useState(50);

  const source = sources.find((m) => `${m.tool}:${m.model}` === fromKey) ?? sources[0];
  const target = targets.find((m) => m.model === to) ?? targets.find((m) => m.model !== source?.pricedAs) ?? targets[0];
  const rangeDays = Math.max(1, (Date.parse(summary.range.to ?? '') - Date.parse(summary.range.from ?? '')) / DAY_MS) || 1;

  if (!settings || !source || !target) {
    return null;
  }

  const fallback = settings.cost.fallbackPricing;
  const fromPrices = resolveModelPricing(source.model, settings.models, fallback).prices;
  const tokens = {
    inputTokens: source.tokens.input,
    outputTokens: source.tokens.output,
    cacheReadTokens: source.tokens.cacheRead,
    cacheWriteTokens: source.tokens.cacheWrite,
  };
  const result = simulateModelSwitch(tokens, fromPrices, target, percent / 100);
  const perMonth = 30 / rangeDays;
  const change = -result.saving;

  return (
    <div className="grid grid-cols-1 gap-5 xl:grid-cols-2">
      <div className="rounded-card border border-border bg-surface p-5 shadow-sm">
        <h3 className="flex items-center gap-1.5 text-sm font-medium text-text-default">
          What-if: switch models
          <InfoTooltip text={SPEND_METRIC_INFO.whatIf} />
        </h3>
        <p className="mt-1 text-xs text-text-muted">Re-price this range&rsquo;s real usage of one model at another model&rsquo;s rates.</p>
        <div className="mt-4 grid grid-cols-1 gap-3 sm:grid-cols-2">
          <label className="text-xs font-medium text-text-muted">
            Move usage from
            <select className={`${fieldClass} mt-1`} value={`${source.tool}:${source.model}`} onChange={(e) => setFromKey(e.target.value)}>
              {sources.map((m) => (
                <option key={`${m.tool}:${m.model}`} value={`${m.tool}:${m.model}`}>
                  {m.model} ({TOOL_LABELS[m.tool]}, {money(m.cost)})
                </option>
              ))}
            </select>
          </label>
          <label className="text-xs font-medium text-text-muted">
            To
            <select className={`${fieldClass} mt-1`} value={target.model} onChange={(e) => setTo(e.target.value)}>
              {targets.map((m) => (
                <option key={m.model} value={m.model}>
                  {m.model} ({m.provider})
                </option>
              ))}
            </select>
          </label>
        </div>
        <label className="mt-4 block text-xs font-medium text-text-muted">
          Share of that usage moved: <span className="font-bold text-text-default">{percent}%</span>
          <input
            type="range"
            min={0}
            max={100}
            step={5}
            value={percent}
            onChange={(e) => setPercent(Number(e.target.value))}
            className="mt-2 block h-11 w-full accent-[var(--primary-blue)]"
          />
        </label>
        <dl className="mt-2 grid grid-cols-1 gap-3 sm:grid-cols-3">
          <Stat label="Now" value={money(result.currentCost)} meta={`${money(result.currentCost * perMonth)} per 30 days`} />
          <Stat label="With the switch" value={money(result.simulatedCost)} meta={`${money(result.simulatedCost * perMonth)} per 30 days`} />
          <Stat
            label={change <= 0 ? 'Saving' : 'Extra cost'}
            value={money(Math.abs(change))}
            meta={`${money(Math.abs(change) * perMonth)} per 30 days${result.currentCost > 0 ? ` · ${Math.round((Math.abs(change) / result.currentCost) * 100)}%` : ''}`}
          />
        </dl>
        <p className="mt-3 text-xs text-text-faint">
          Indicative. Assumes the moved work uses the same tokens on {target.model}; real token use and output quality differ between models.
        </p>
      </div>

      <TableCard title="Cost per session, by model" info={SPEND_METRIC_INFO.costPerSession} empty={sources.length === 0} emptyText="No model usage in this range.">
        <thead className="border-b border-border text-[11px] uppercase tracking-wide text-text-faint">
          <tr>
            <th className="pb-2.5 pr-3">Model</th>
            <th className="pb-2.5 pr-3">Tool</th>
            <th className="pb-2.5 pr-3 text-right">Sessions</th>
            <th className="pb-2.5 pr-3 text-right">Cost</th>
            <th className="pb-2.5 text-right">Per session</th>
          </tr>
        </thead>
        <tbody>
          {sources.map((m) => (
            <tr key={`${m.tool}:${m.model}`} className="border-b border-border last:border-0">
              <td className="py-2.5 pr-3 text-text-default">{m.model}</td>
              <td className="py-2.5 pr-3 text-text-muted">{TOOL_LABELS[m.tool]}</td>
              <td className="py-2.5 pr-3 text-right tabular-nums">{m.sessions.toLocaleString()}</td>
              <td className="py-2.5 pr-3 text-right tabular-nums">{money(m.cost)}</td>
              <td className="py-2.5 text-right font-medium tabular-nums text-text-default">{m.sessions > 0 ? money(m.cost / m.sessions) : '—'}</td>
            </tr>
          ))}
        </tbody>
      </TableCard>
    </div>
  );
}

function Stat({ label, value, meta }: { label: string; value: string; meta: string }) {
  return (
    <div className="rounded-input bg-surface-alt p-3">
      <dt className="text-[11px] uppercase tracking-wide text-text-faint">{label}</dt>
      <dd className="text-xl font-bold tabular-nums text-text-default">{value}</dd>
      <dd className="text-xs text-text-muted">{meta}</dd>
    </div>
  );
}
